import { api } from '@/api';
import { createEventHook } from '@vueuse/core';
import { DateTime } from 'luxon';
import {
  computed,
  MaybeRefOrGetter,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  toValue,
  watch,
} from 'vue';
import { DISABLE_BEFOREUNLOAD_WARNING } from '@/env';
import { objectFromKeys } from '../helper';
import { mapValues } from 'lodash';
import { FetchError, EntityLockTokenDto } from '@/api/generator';
import { isApiError, stopErrorPropagation } from '@/errorHandling';

export type EntityWithToken = { id?: string | number; $type?: string };

const tokenEvents = [
  'requested',
  'revoked',
  'renewed',
  'inUse',
  'noConnection',
  'initialized',
] as const;
export type TokenEvent = (typeof tokenEvents)[number];

export function hasIdAndType(entity?: any): entity is Required<EntityWithToken> {
  return entity.id !== undefined && entity.$type !== undefined;
}

function isTokenExpired(token: EntityLockTokenDto) {
  return (
    DateTime.fromISO(token.lastRequest).plus({ seconds: token.renewalTimeout }) < DateTime.now()
  );
}

function throwFetchErrorHandler(error: Error) {
  if (error instanceof FetchError) {
    throw error;
  } else {
    return error;
  }
}

class TokenInUseError extends Error {
  token?: EntityLockTokenDto;

  constructor(token?: EntityLockTokenDto) {
    super('Token already exists');
    this.name = 'TokenInUseError';
    this.token = token;
  }
}

function tokenInUseErrorHandler(error: Error) {
  if (isApiError(error) && error.response.status === 409) {
    const token = error?.errorResponse?.data?.token;
    throw new TokenInUseError(token);
  }

  return error;
}

async function requestToken(entity: EntityWithToken, signal: AbortSignal) {
  if (hasIdAndType(entity)) {
    const { result: token } = await api.entityLockTokensApi.requestToken(
      {
        entityLockTokenInputDto: {
          entityId: entity.id.toString(),
          entityType: entity.$type,
        },
      },
      [tokenInUseErrorHandler, throwFetchErrorHandler, stopErrorPropagation],
      { signal },
    );

    return token;
  }
}

async function renewToken(tokenId: string, signal?: AbortSignal) {
  const { result: token } = await api.entityLockTokensApi.updateToken(
    {
      tokenId,
    },
    [tokenInUseErrorHandler, throwFetchErrorHandler, stopErrorPropagation],
    { signal },
  );

  return token;
}

async function revokeToken(tokenId: string) {
  const { success } = await api.entityLockTokensApi.deleteToken(
    {
      tokenId,
    },
    [tokenInUseErrorHandler, throwFetchErrorHandler],
  );

  return success;
}

function getReducedEntityWithToken(entity: EntityWithToken | undefined) {
  const entityRef = ref<EntityWithToken | undefined>(undefined);

  watch(
    () => ({ id: entity?.id, $type: entity?.$type }),
    (entityWithToken) => {
      entityRef.value = hasIdAndType(entityWithToken) ? entityWithToken : undefined;
    },
    {
      immediate: true,
    },
  );

  return entityRef;
}

export function useToken(entity: MaybeRefOrGetter<EntityWithToken | undefined>) {
  const retryIntervalSeconds = 5;

  const entityRef = getReducedEntityWithToken(toValue(entity));

  const currentToken = ref<EntityLockTokenDto>();

  const inUse = ref<boolean>(false);

  let repeat: ReturnType<typeof setTimeout> | undefined = undefined;

  const currentRenewalInterval = computed(() =>
    currentToken.value?.renewalTime ? currentToken.value.renewalTime * 1000 : undefined,
  );

  const isValid = computed(() => !!currentToken.value && !isTokenExpired(currentToken.value));

  const onUpdateHook = createEventHook<{ event: TokenEvent; token?: EntityLockTokenDto }>();

  const tokenEventHooks = objectFromKeys(
    [...tokenEvents],
    createEventHook<EntityLockTokenDto | undefined>,
  );

  function triggerUpdate(event: TokenEvent, token = currentToken.value) {
    onUpdateHook.trigger({ event, token });
    tokenEventHooks[event].trigger(token);
  }

  const requestController = new AbortController();

  async function request(
    renewal: 'manual' | 'automatic' = 'automatic',
    retryInterval = retryIntervalSeconds * 1000,
  ) {
    if (!entityRef.value) {
      // when entity not set yet wait for it and try again one more time
      watch(entityRef, async () => await request(renewal, retryInterval), { once: true });
      return;
    }

    try {
      const oldToken = currentToken.value;
      const newToken = await requestToken(entityRef.value, requestController.signal);

      if (newToken) {
        currentToken.value = newToken;

        if (!oldToken) {
          triggerUpdate('initialized');
        }

        triggerUpdate('requested');

        if (renewal === 'automatic' && !repeat) {
          await renew('afterRenewalTime');
        }
      }
    } catch (error: any) {
      switch (error.constructor) {
        case TokenInUseError: {
          triggerUpdate('inUse', error.token);
          inUse.value = true;
          break;
        }

        case FetchError: {
          triggerUpdate('noConnection');
          repeat = setTimeout(async () => await request(renewal), retryInterval);
          break;
        }
      }
    }
  }

  async function renew(
    start: 'immediate' | 'afterRenewalTime' = 'immediate',
    retryInterval = retryIntervalSeconds * 1000,
  ) {
    if (!currentToken.value) {
      return;
    }

    if (start === 'afterRenewalTime') {
      clearTimeout(repeat);
      repeat = setTimeout(async () => await renew('immediate'), currentRenewalInterval.value);
      return;
    }

    try {
      const updatedToken = await renewToken(currentToken.value.id, requestController.signal);

      if (updatedToken) {
        currentToken.value = updatedToken;

        triggerUpdate('renewed');

        await renew('afterRenewalTime');
      } else {
        request('automatic');
      }
    } catch (error: any) {
      switch (error.constructor) {
        case TokenInUseError: {
          triggerUpdate('inUse');
          inUse.value = true;
          break;
        }

        case FetchError: {
          triggerUpdate('noConnection');
          repeat = setTimeout(async () => await renew(start), retryInterval);
          break;
        }
      }
    }
  }

  async function revoke() {
    requestController.abort();
    clearTimeout(repeat);

    const tokenId = currentToken.value?.id;

    if (!tokenId) {
      return;
    }

    try {
      const revoked = await revokeToken(tokenId);
      currentToken.value = undefined;

      if (revoked) {
        inUse.value = false;
        triggerUpdate('revoked');
      }
    } catch (error: any) {
      switch (error.constructor) {
        case TokenInUseError: {
          triggerUpdate('inUse', error.token);
          inUse.value = true;
          break;
        }

        case FetchError: {
          triggerUpdate('noConnection');
          break;
        }
      }
    }
  }

  watch(entityRef, async (_newEntity, oldEntity) => {
    if (oldEntity) {
      await revoke();
    }
  });

  async function beforeUnloadHandler(ev: BeforeUnloadEvent) {
    if (DISABLE_BEFOREUNLOAD_WARNING) {
      ev.stopImmediatePropagation();
    }

    await revoke();
  }

  onBeforeMount(() => {
    window.addEventListener('beforeunload', beforeUnloadHandler);
  });

  onBeforeUnmount(async () => {
    window.removeEventListener('beforeunload', beforeUnloadHandler);
    await revoke();
  });

  return {
    request,
    revoke,
    renew,
    onUpdate: onUpdateHook.on,
    on: mapValues(tokenEventHooks, (hook) => hook.on),
    isValid,
    inUse,
    currentToken,
  };
}
